import { atom, Atom } from "tldraw";
import type { TldrawAgent } from "../TldrawAgent";
import { BaseAgentManager } from "./BaseAgentManager";

export type AgentCursorState =
  | "idle"
  | "listening"
  | "thinking"
  | "creating"
  | "drawing"
  | "editing"
  | "deleting"
  | "moving"
  | "generating";

/**
 * Manages the visual cursor overlay for an agent on the canvas.
 * Tracks cursor visibility, position, and current state label.
 */
export class AgentCursorManager extends BaseAgentManager {
  private $visible: Atom<boolean>;
  private $position: Atom<{ x: number; y: number } | null>;
  private $state: Atom<AgentCursorState>;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(agent: TldrawAgent) {
    super(agent);
    this.$visible = atom("cursorVisible", false);
    this.$position = atom("cursorPosition", null);
    this.$state = atom("cursorState", "idle" as AgentCursorState);
  }

  reset(): void {
    this.clearHideTimeout();
    this.$visible.set(false);
    this.$position.set(null);
    this.$state.set("idle");
  }

  /**
   * Show cursor at viewport center with "thinking" state.
   * Used at the start of a request before any actions arrive.
   */
  showThinking(): void {
    this.clearHideTimeout();
    const editor = this.agent.editor;
    const viewportBounds = editor.getViewportPageBounds();
    this.$position.set({
      x: viewportBounds.center.x,
      y: viewportBounds.center.y,
    });
    this.$state.set("thinking");
    this.$visible.set(true);
  }

  /**
   * Show cursor at viewport center with "listening" state.
   * Used when the user starts recording audio.
   */
  showListening(): void {
    this.clearHideTimeout();
    const editor = this.agent.editor;
    const viewportBounds = editor.getViewportPageBounds();
    this.$position.set({
      x: viewportBounds.center.x,
      y: viewportBounds.center.y,
    });
    this.$state.set("listening");
    this.$visible.set(true);
  }

  /**
   * Hide the cursor with a fade-out transition.
   * Sets state to "idle" immediately (triggering CSS opacity fade),
   * then removes the element after the transition completes.
   */
  hide(): void {
    this.clearHideTimeout();
    this.$state.set("idle");
    this.hideTimeout = setTimeout(() => {
      this.$visible.set(false);
      this.$position.set(null);
      this.hideTimeout = null;
    }, 300);
    // Track timeout for cleanup on dispose
    const timeout = this.hideTimeout;
    this.disposables.add(() => {
      if (timeout) clearTimeout(timeout);
    });
  }

  /** Set the target position; a write with unchanged coordinates is a no-op. */
  setPosition(pos: { x: number; y: number }): void {
    const current = this.$position.__unsafe__getWithoutCapture();
    if (current && current.x === pos.x && current.y === pos.y) return;
    this.$position.set(pos);
  }

  /**
   * The target position, read without subscribing.
   *
   * For the overlay's animation loop: a reactive read there would re-render it.
   */
  getPositionUntracked(): { x: number; y: number } | null {
    return this.$position.__unsafe__getWithoutCapture();
  }

  setState(state: AgentCursorState): void {
    this.clearHideTimeout();
    this.$state.set(state);
  }

  /** For useValue() consumption in React */
  isVisible(): boolean {
    return this.$visible.get();
  }

  /** For useValue() consumption in React */
  getState(): AgentCursorState {
    return this.$state.get();
  }

  private clearHideTimeout(): void {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }
}
